import {Loader2, Plus} from "lucide-react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {toast} from "sonner";

import ImagePicker from "@/components/ui/image-picker";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {Loader} from "@/src/modules/core/components";
import useDebounce from "@/src/modules/core/hooks/use-debounce";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {US_STATE_OPTIONS, type UsStateOption} from "@/src/modules/core/lib/us-state-options";
import {errorHandler} from "@/src/modules/core/lib/tanstack-error";
import {useStoredMe} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {useCreateFiles} from "@/src/modules/files/hooks/use-create-files";
import {useActiveShift} from "@/src/modules/officer/hooks/use-active-shift";
import {VehiclesFormSheet} from "@/src/modules/vehicles/components/form-sheet";
import {vehiclesService} from "@/src/modules/vehicles/services";

import {VEHICLE_CHECK_RECOGNITION_STATUS_LABELS} from "../lib/constants";
import {VehicleCheckMatchCard} from "./vehicle-check-match-card";
import {extractResumableCheckId} from "../lib/vehicle-check-errors";
import {useVehicleCheckCreate} from "../hooks/use-vehicle-check-create";
import {vehicleChecksService} from "../services";
import {useVehicleCheckLookup} from "../hooks/use-vehicle-check-lookup";
import {useVehicleCheckStatus} from "../hooks/use-vehicle-check-status";
import type {VehicleCheck, VehicleCheckMatch, VehicleCheckRecognition} from "../types";

type CapturePhase = "capture" | "processing" | "review";

type VehicleCheckCaptureSheetProps = {
  open: boolean;
  onClose: () => void;
};

function formatConfidence(value?: number | null) {
  if (value == null || Number.isNaN(value)) return null;
  const normalized = value > 1 ? value : value * 100;
  return `${Math.round(normalized)}%`;
}

function isLookupReady(plate: string, state: string) {
  return plate.trim().length >= 2 && state.trim().length === 2;
}

export function VehicleCheckCaptureSheet({open, onClose}: VehicleCheckCaptureSheetProps) {
  const navigate = useNavigate();
  const me = useStoredMe();
  const userProperty = useStoredUserProperty();
  const {shift} = useActiveShift();
  const {mutateAsync: createFiles} = useCreateFiles();
  const {mutateAsync: createVehicleCheck} = useVehicleCheckCreate();
  const {mutateAsync: lookupVehicle} = useVehicleCheckLookup();
  const {mutateAsync: updateCheckStatus, isPending: isStatusPending} = useVehicleCheckStatus();
  const debouncedLookup = useDebounce(400);

  const [phase, setPhase] = useState<CapturePhase>("capture");
  const [processingLabel, setProcessingLabel] = useState("");
  const [images, setImages] = useState<(File | string)[]>([]);
  const [checkId, setCheckId] = useState<number | null>(null);
  const [vehicleCheck, setVehicleCheck] = useState<VehicleCheck | null>(null);
  const [recognition, setRecognition] = useState<VehicleCheckRecognition | null>(null);
  const [captureUrl, setCaptureUrl] = useState<string | null>(null);
  const [plate, setPlate] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [matches, setMatches] = useState<VehicleCheckMatch[]>([]);
  const [lookupPending, setLookupPending] = useState(false);
  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);
  const [checkedVehicleIds, setCheckedVehicleIds] = useState<Set<number>>(() => new Set());
  const [checkWithoutVehicleDone, setCheckWithoutVehicleDone] = useState(false);
  const checkedVehicleIdsRef = useRef<Set<number>>(new Set());
  const checkInFlightRef = useRef<Set<number>>(new Set());
  const checkWithoutVehicleInFlightRef = useRef(false);
  const lookupRequestId = useRef(0);

  const selectedFile = useMemo(
    () => images.find((image): image is File => image instanceof File) ?? null,
    [images],
  );

  const selectedState: UsStateOption | null = useMemo(() => {
    const code = stateCode.trim().toUpperCase();
    return code ? (US_STATE_OPTIONS.find((option) => option.value === code) ?? null) : null;
  }, [stateCode]);

  const resetSheet = useCallback(() => {
    setPhase("capture");
    setProcessingLabel("");
    setImages([]);
    setCheckId(null);
    setVehicleCheck(null);
    setRecognition(null);
    setCaptureUrl(null);
    setPlate("");
    setStateCode("");
    setMatches([]);
    setLookupPending(false);
    setCreateVehicleOpen(false);
    setCheckedVehicleIds(new Set());
    setCheckWithoutVehicleDone(false);
    checkedVehicleIdsRef.current = new Set();
    checkInFlightRef.current = new Set();
    checkWithoutVehicleInFlightRef.current = false;
    lookupRequestId.current = 0;
  }, []);

  useEffect(() => {
    if (!open) {
      resetSheet();
    }
  }, [open, resetSheet]);

  const runLookup = useCallback(
    async (id: number, plateValue: string, stateValue: string) => {
      if (!isLookupReady(plateValue, stateValue)) {
        setMatches([]);
        return;
      }

      const requestId = ++lookupRequestId.current;
      setLookupPending(true);
      try {
        const result = await lookupVehicle({
          checkId: id,
          payload: {
            plate: plateValue.trim().toUpperCase(),
            state: stateValue.trim().toUpperCase(),
          },
        });
        if (requestId !== lookupRequestId.current) return;
        setMatches(result.matches ?? []);
        setVehicleCheck(result.vehicle_check);
      } finally {
        if (requestId === lookupRequestId.current) {
          setLookupPending(false);
        }
      }
    },
    [lookupVehicle],
  );

  useEffect(() => {
    if (phase !== "review" || !checkId) return;
    if (!isLookupReady(plate, stateCode)) {
      setMatches([]);
      return;
    }

    debouncedLookup(() => {
      void runLookup(checkId, plate, stateCode);
    });
  }, [phase, checkId, plate, stateCode, debouncedLookup, runLookup]);

  const beginReview = useCallback(
    (
      nextCheck: VehicleCheck,
      fileUrl?: string | null,
    ) => {
      const nextRecognition = nextCheck.recognition;
      const nextPlate = (nextRecognition.plate ?? nextCheck.plate ?? "").trim().toUpperCase();
      const nextState = (nextRecognition.state ?? nextCheck.state ?? "").trim().toUpperCase();
      setCheckId(nextCheck.vehicle_check_id);
      setVehicleCheck(nextCheck);
      setRecognition(nextRecognition);
      setCaptureUrl(fileUrl ?? nextCheck.files?.[0] ?? null);
      setPlate(nextPlate);
      setStateCode(nextState);
      setMatches(nextCheck.matches ?? []);
      setPhase("review");
    },
    [],
  );

  const handleAnalyze = async () => {
    if (!selectedFile || !userProperty?.id || phase === "processing") return;

    try {
      setPhase("processing");
      setProcessingLabel("Uploading photo...");
      const uploadedUrls = await createFiles({
        files: [selectedFile],
        upload_context: "vehicle_check",
      });

      if (!uploadedUrls.length) {
        toast.error("Upload failed. Please try again.");
        setPhase("capture");
        setProcessingLabel("");
        return;
      }

      setProcessingLabel("Analyzing plate...");
      const gps = await resolveGpsForSubmit(
        {propertyCoordinates: userProperty.gps_coordinates ?? null},
        {
          onFailure: "silent",
          captureContext: {feature: "vehiclechecks", operation: "vehicle_check_create"},
        },
      );

      const result = await createVehicleCheck(
        vehicleChecksService.buildPhotoCreatePayload(me, {
          property_id: userProperty.id,
          files: [uploadedUrls[0]],
          shift,
          gps_coordinates: gps?.gps_coordinates,
          gps_source: gps?.gps_source,
        }),
      );

      beginReview(result.vehicle_check, result.vehicle_check.files?.[0] ?? uploadedUrls[0]);
      setProcessingLabel("");
    } catch (error) {
      const resumable = extractResumableCheckId(error);
      if (resumable) {
        toast.warning(resumable.message || "Plate not recognized. Enter it manually.");
        beginReview({
          vehicle_check_id: resumable.checkId,
          recognition: {
            status: resumable.errorCode,
            plate: null,
            state: null,
            confidence: null,
            candidates: [],
          },
        });
        setProcessingLabel("");
        return;
      }

      errorHandler(error);
      setPhase("capture");
      setProcessingLabel("");
    }
  };

  const completeCheckForVehicle = useCallback(
    async (vehicleId: number) => {
      if (!checkId) return;
      if (
        checkedVehicleIdsRef.current.has(vehicleId) ||
        checkInFlightRef.current.has(vehicleId)
      ) {
        return;
      }

      checkInFlightRef.current.add(vehicleId);
      try {
        const updated = await updateCheckStatus({
          checkId,
          payload: {status: "check", vehicle_id: vehicleId},
        });
        setVehicleCheck(updated);
        checkedVehicleIdsRef.current.add(vehicleId);
        setCheckedVehicleIds(new Set(checkedVehicleIdsRef.current));
      } finally {
        checkInFlightRef.current.delete(vehicleId);
      }
    },
    [checkId, updateCheckStatus],
  );

  const completeCheckWithoutVehicle = useCallback(async () => {
    if (!checkId || checkWithoutVehicleDone || checkWithoutVehicleInFlightRef.current) return;

    checkWithoutVehicleInFlightRef.current = true;
    try {
      const updated = await updateCheckStatus({
        checkId,
        payload: {status: "check"},
      });
      setVehicleCheck(updated);
      setCheckWithoutVehicleDone(true);
    } finally {
      checkWithoutVehicleInFlightRef.current = false;
    }
  }, [checkId, checkWithoutVehicleDone, updateCheckStatus]);

  const handleRecordCheck = async (vehicleId: number) => {
    if (!checkId || isStatusPending || checkedVehicleIds.has(vehicleId)) return;
    await completeCheckForVehicle(vehicleId);
    toast.success("Check recorded");
  };

  const handleViewVehicle = async (vehicleId: number) => {
    if (!checkId || isStatusPending) return;
    await completeCheckForVehicle(vehicleId);
    onClose();
    navigate(`/parking-enforcement/${vehicleId}`);
  };

  const handleCreateVehicle = async () => {
    if (!checkId || isStatusPending || checkWithoutVehicleDone) return;
    await completeCheckWithoutVehicle();
    setCreateVehicleOpen(true);
  };

  const handleClose = () => {
    if (phase === "processing") return;
    onClose();
  };

  const canCreateVehicle = vehiclesService.getIsCreateEnabled();
  const recognitionStatus = recognition?.status ?? "pending";
  const confidenceLabel = formatConfidence(recognition?.confidence);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) handleClose();
        }}
      >
        <SheetContent
          side="right"
          className="flex min-h-0 w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        >
          <div className="border-b border-border px-4 py-4">
            <h2 className="text-lg font-semibold">Scan plate</h2>
            <p className="text-sm text-muted-foreground">
              Capture a plate photo, review the read, and match it to a vehicle on file.
            </p>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-4">
            {phase === "capture" ? (
              <ImagePicker
                images={images}
                maxFiles={1}
                immediateUpload={false}
                onUpload={setImages}
                onDelete={(index: number) =>
                  setImages((current) => current.filter((_, i) => i !== index))
                }
                onBeforePick={async () => {}}
                errors={[]}
                captureFeature="vehiclechecks"
                captureOperation="vehicle_check_capture"
              />
            ) : null}

            {phase === "processing" ? (
              <div className="flex min-h-40 flex-col items-center justify-center gap-3">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{processingLabel}</p>
              </div>
            ) : null}

            {phase === "review" ? (
              <>
                {captureUrl ? (
                  <img
                    src={captureUrl}
                    alt="Plate capture"
                    className="h-40 w-full rounded-md border border-border object-cover"
                  />
                ) : null}

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">
                    {VEHICLE_CHECK_RECOGNITION_STATUS_LABELS[recognitionStatus] ??
                      recognitionStatus}
                  </Badge>
                  {confidenceLabel ? (
                    <span className="text-xs text-muted-foreground">
                      Confidence {confidenceLabel}
                    </span>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="vehicle-check-plate">Plate</Label>
                    <Input
                      id="vehicle-check-plate"
                      value={plate}
                      autoCapitalize="characters"
                      onChange={(event) => setPlate(event.target.value.toUpperCase())}
                    />
                  </div>
                  <div className="flex min-w-0 flex-col gap-2">
                    <Label>State</Label>
                    <ComboboxGeneric<UsStateOption>
                      className="w-full min-w-0 normal-case"
                      placeholder="Select state"
                      emptyMessage="No state matches."
                      items={US_STATE_OPTIONS}
                      value={selectedState}
                      onChange={(option, details) => {
                        if (details.reason === "none") return;
                        setStateCode(option?.value ?? "");
                      }}
                      getItemKey={(option) => option.value}
                      getItemLabel={(option) => option.label}
                      isItemEqual={(a, b) => a?.value === b?.value}
                      preserveCase
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium">Property matches</p>

                  {lookupPending ? (
                    <Loader className="min-h-24 w-full" size="sm" />
                  ) : matches.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {matches.map((match) => (
                        <VehicleCheckMatchCard
                          key={`${match.vehicle.vehicle_id}-${match.matched_plate}`}
                          match={match}
                          vehicleCheck={vehicleCheck}
                          isStatusPending={isStatusPending}
                          isChecked={checkedVehicleIds.has(match.vehicle.vehicle_id)}
                          onCheck={() => void handleRecordCheck(match.vehicle.vehicle_id)}
                          onView={() => void handleViewVehicle(match.vehicle.vehicle_id)}
                        />
                      ))}
                    </div>
                  ) : isLookupReady(plate, stateCode) ? (
                    <p className="text-sm text-muted-foreground">
                      No matching vehicle on this property.
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Enter a plate and state to search this property.
                    </p>
                  )}

                  {!lookupPending &&
                  matches.length === 0 &&
                  isLookupReady(plate, stateCode) &&
                  canCreateVehicle ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={isStatusPending}
                      onClick={() => void handleCreateVehicle()}
                    >
                      <Plus className="size-4" />
                      New vehicle
                    </Button>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>

          {phase === "capture" ? (
            <SheetFooter className="border-t border-border px-4 py-4">
              <Button variant="outline" className="w-full sm:w-auto" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={() => void handleAnalyze()}
                disabled={!selectedFile}
              >
                Analyze plate
              </Button>
            </SheetFooter>
          ) : null}

          {phase === "review" ? (
            <SheetFooter className="border-t border-border px-4 py-4">
              <Button variant="outline" className="w-full" onClick={handleClose}>
                Close
              </Button>
            </SheetFooter>
          ) : null}
        </SheetContent>
      </Sheet>

      {createVehicleOpen ? (
        <VehiclesFormSheet
          open
          prefill={{plate: plate.trim().toUpperCase(), state: stateCode.trim().toUpperCase()}}
          onClose={() => {
            setCreateVehicleOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
